import * as React from "react";

import type {ReadingStyle, Voice} from "@khan/guide-types";

import type {SetGuidePreferencesVariables} from "./__graphql-types__/SetGuidePreferences.ts";

export interface GuidePreferences {
    readingLevel: ReadingStyle;
    guideLanguageCode: string;
    guideVoiceDescriptor: Voice | null;
    showDABs: boolean | null;
}

export const DEFAULT_GUIDE_PREFERENCES = {
    readingLevel: "LOW" as ReadingStyle,
    guideLanguageCode: "en",
    guideVoiceDescriptor: null,
    showDABs: null,
};

export interface GuidePreferencesContextState {
    isLoading: boolean;
    preferences: GuidePreferences;
    refetchPreferences: () => void;
    setPreferences: (
        preferences: SetGuidePreferencesVariables,
    ) => Promise<void>;
    resetLanguagePreferences: () => Promise<void>;
}

/**
 * The default state of the guide preferences context.
 */
const defaultState: GuidePreferencesContextState = Object.freeze({
    isLoading: false,
    preferences: DEFAULT_GUIDE_PREFERENCES,
    refetchPreferences: () => {},
    setPreferences: () => Promise.resolve(),
    resetLanguagePreferences: () => Promise.resolve(),
});

/**
 * A React context that provides access to the user's guide preferences.
 *
 * Please do not use this directly. Instead use the GuidePreferencesProvider
 * and the useGuidePreferences hook.
 */
export const GuidePreferencesContext: React.Context<GuidePreferencesContextState> =
    React.createContext(defaultState);
GuidePreferencesContext.displayName = "GuidePreferencesContext";
